/**
 * 학생 앱의 전역 상태(Recoil atom) 모음. 앱 전체가 이 한 파일을 공유한다.
 *
 * 크게 세 덩어리다. 회원가입 입력값(formDataState·termsState·userTypeState),
 * 문제 풀이 진행 상태(problemDataState·tempDataState·answerDataState·chapterTimeState),
 * 그리고 세션·네트워크 상태(userState·networkStatusState)다.
 *
 * 주의사항 두 가지가 있다. 둘 다 코드만 봐서는 알기 어렵다.
 *
 * 1. `effects_UNSTABLE: [persistAtom]` 이 붙은 atom 은 localStorage 에 저장된다.
 *    브라우저를 닫았다 열어도 값이 남는다는 뜻이다. 그래서 atom 의 `key` 문자열을 바꾸면
 *    옛 key 로 저장된 데이터가 localStorage 에 유령처럼 남는다. key 를 바꿔야 한다면
 *    기존 값을 옮기거나 지우는 코드를 같이 넣어야 한다.
 * 2. 아래에서 중복 key 검사를 꺼 두었다. 실수로 같은 key 를 두 번 쓰면 경고 없이
 *    조용히 덮어써진다. 새 atom 을 추가하기 전에 key 문자열을 먼저 검색해 보라.
 */
 // 프론트 테스트
import { atom, RecoilEnv } from "recoil";
import { recoilPersist } from "recoil-persist";
import { Question } from "@/types/problemProps";
import { TempAnswerProps } from "@/types/tempAnswerProps";
import { AnswerProps } from "@/types/answerProps";
import { Problem } from "@/types/chapterDetailProps";
import { UserProps } from "@/types/userProps";
import { AuditLogPayload } from "@/types/auditLogProps";

// 중복 atom key 경고를 끈다. 개발 중 파일이 다시 불러와질 때 뜨는 시끄러운 경고를 없애려는 설정이다.
// 대신 진짜 key 충돌도 조용히 넘어가니, 새 atom 을 만들 때 key 를 직접 검색해 확인해야 한다.
RecoilEnv.RECOIL_DUPLICATE_ATOM_KEY_CHECKING_ENABLED = false;

// 이 효과를 붙인 atom 은 값이 바뀔 때마다 localStorage 에 저장되고, 새로고침하면 저장된 값으로 되살아난다.
// 문제를 풀다가 새로고침해도 답안과 타이머가 남아 있어야 하기 때문에 쓴다.
const { persistAtom } = recoilPersist();

// 네트워크 상태 관리
export type NetworkStatus = "normal" | "unstable" | "offline" | "recovered";

export const networkStatusState = atom<NetworkStatus>({
  key: "networkStatusState",
  default: "normal",
});

// 감사 로그 전송 대기 큐
export const auditLogQueueState = atom<AuditLogPayload[]>({
  key: "auditLogQueueState",
  default: [],
  effects_UNSTABLE: [persistAtom],
});

// 마지막으로 로그인했던 유저 ID (기기 전환/유저 전환 감지용)
export const lastUserIdState = atom<number | null>({
  key: "lastUserIdState",
  default: null,
  effects_UNSTABLE: [persistAtom],
});

// 휴대폰 인증 시, 보호자 휴대폰 저장
export const parentPhoneState = atom({
  key: "parentPhoneState",
  default: "",
  effects_UNSTABLE: [persistAtom],
});

// for 회원가입 (가입 후에는 초기화 필요)
// 0: 타입 선택 전, 1: 일반회원, 2: 어린이 회원
export const userTypeState = atom({
  key: "userTypeState",
  default: 0,
  effects_UNSTABLE: [persistAtom],
});

// 회원가입 진행 단계. 현재 이 파일 밖에서 읽거나 쓰는 곳이 없다.
// 지금은 쓰이지 않지만 임의로 지우지 않고 남겨 둔다.
export const signProgressState = atom({
  key: "signProgressState",
  default: 0,
  effects_UNSTABLE: [persistAtom],
});

// 이용약관 동의
export const termsState = atom({
  key: "termsState", // 고유한 키 값
  default: [
    { id: 1, isAgree: false }, // 이용약관 동의
    { id: 2, isAgree: false }, // 개인정보 수집 동의
    { id: 3, isAgree: false }, // 광고성 정보 수신 동의
  ],
  effects_UNSTABLE: [persistAtom],
});

// 회원가입에서 입력한 로그인 아이디.
// 입력 화면(signin/userInfo)에서 채우고 가입 완료 화면(signin/complete)에서 다시 보여 준다.
export const idState = atom({
  key: "idState",
  default: "",
  effects_UNSTABLE: [persistAtom],
});

// 로그인 후, 백에서 전달받는 유저정보
export const userState = atom<UserProps>({
  key: "userState",
  default: {
    id: 0,
    email: "",
    name: "",
    school: "",
    grade: 0,
    phoneNumber: "",
    birthDate: "",
    gender: "",
    address: "",
    postalCode: "",
    detailAddress: "",
    profileImageUrl: "",
    secondaryEmail: "",
  },
  effects_UNSTABLE: [persistAtom],
});

// 클라이언트에서 회원가입 시, 입력하는 개인정보 (모바일 리다이렉션 후, 입력 정보 원복을 위한 변수)
export const formDataState = atom({
  key: "formDataState", // 고유한 키
  default: {
    id: "",
    password: "",
    confirmPassword: "",
    name: "",
    // school_id 단일화: 학교급 문자열(`school`)에서 schoolId + 표시용 이름으로 교체.
    // 이 atom 은 recoil-persist 로 localStorage 에 남는다. 예전 형태({school: "초등학교"})가
    // 저장된 브라우저에서는 복원 시 schoolId 가 undefined 가 되므로,
    // 복원하는 쪽(userInfo.tsx)에서 null 로 정규화한다. atom key 는 그대로라 좀비 데이터는 없다.
    schoolId: null as number | null,
    schoolName: "",
    grade: "",
    emailFirst: "",
    emailDomain: "기타", // 선택된 도메인 (기타를 포함) (기본값을 기타로 설정)
    customEmailDomain: "", // 직접 입력하는 커스텀 도메인
    phoneFirst: "",
    phoneMiddle: "",
    phoneLast: "",
    birthDate: "",
    gender: "",
    address: "",
    postalCode: "",
    detailAddress: "",
    termsOfUseCheckInfoList: [],
  },
  effects_UNSTABLE: [persistAtom],
});

// fetchProblems 데이터 저장
export const problemDataState = atom<Question[]>({
  key: "problemDataState",
  default: [],
  effects_UNSTABLE: [persistAtom],
});

// temp 문제 답안 데이터 저장
export const tempDataState = atom<TempAnswerProps[]>({
  key: "tempDataState",
  default: [],
  effects_UNSTABLE: [persistAtom],
});

// 문제 답안 데이터 저장
export const answerDataState = atom<AnswerProps[]>({
  key: "answerDataState",
  default: [],
  effects_UNSTABLE: [persistAtom],
});

// 답안 제출 여부 (전체 문제 풀이 완료 여부)
export const isAnswerSubmittedState = atom({
  key: "isAnswerSubmittedState",
  default: false,
  effects_UNSTABLE: [persistAtom],
});

// 그만 풀기 후 다시 문제 푸는 여부
export const retryState = atom<boolean>({
  key: "retryState",
  default: false,
  effects_UNSTABLE: [persistAtom],
});

// 문제 일시정지 여부
export const problemPauseState = atom({
  key: "problemPauseState",
  default: false,
  effects_UNSTABLE: [persistAtom],
});

// 답변이 없는 문제의 인덱스를 관리하는 상태
export const unansweredQuestionsState = atom<number[]>({
  key: "unansweredQuestionsState", // 고유 key
  default: [], // 초기값은 빈 배열
  effects_UNSTABLE: [persistAtom],
});

// 사용자가 풀고 있는 현재 문제 인덱스 저장용
export const currentQuestionIndexState = atom<number>({
  key: "currentQuestionIndexState",
  default: 0,
  effects_UNSTABLE: [persistAtom],
});

// 챕터 상세 데이터
export const chapterDetailState = atom<Problem | null>({
  key: "chapterDetailState",
  default: null, // 초기값은 null로 설정
  effects_UNSTABLE: [persistAtom],
});

// 전체 챕터 풀이 시간을 저장
export const chapterTimeState = atom({
  key: "chapterTimeState",
  default: 0,
  effects_UNSTABLE: [persistAtom],
});

// 챕터 이름
export const chapterNameState = atom({
  key: "chapterNameState",
  default: "",
  effects_UNSTABLE: [persistAtom],
});

// 현재 활성화된 챕터 ID
export const currentChapterIdState = atom<number | null>({
  key: "currentChapterIdState",
  default: null,
  effects_UNSTABLE: [persistAtom],
});

// 체험용 계정 접근 제한 모달에 표시할 대상 박스 이름 (null = 닫힘, 비영속, 세션 저장 안 함)
export const trialBlockModalState = atom<string | null>({
  key: "trialBlockModalState",
  default: null,
});


